import React, { useMemo, useState } from 'react';
import { SxProps, Theme } from '@mui/material';
import Box from '@components/Atoms/Box';
import ShowIf from '@components/Atoms/ShowIf';
import SectionWrapper from '@components/Atoms/SectionWrapper';
import Typography from '@components/Atoms/Typography';
import { useIsTeacher } from '@reducers/user/hooks';
import { useLearningOutcomeReportsAccess } from '@components/Component/LearningOutcomeReports/IsLearningOutcomeReportsFlagEnabled/IsLearningOutcomeReportsFlagEnabled';
import ScrollWrapper from '@components/Atoms/ScrollWrapper';
import Button from '@components/Atoms/Button/Button';
import VisibilityIcon from '@mui/icons-material/Visibility';
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff';
import { CurriculumSubjectReportTabs, TestletReportTabs, getReportSubjects } from './Reports';
import { LearnosityExamReport, ResponseAnalysisReport } from './LearnsoityReports';
import TestBreadcrumbsFilter from '../TestBreadCrumbsFilter';
import SkillReport from './SkillReport';
import LessonReport from './LessonReport';
import { HighlightedScoreProvider } from '../../contexts/HighlightedScore/HighlightedScoreContext';
import { HideEmptyColumnsProvider } from '../../contexts/HideEmptyColumns/HideEmptyColumnsContext';
import StandardReport from './StandardReport';
import ExportCsvButton from './ExportCsvButton';
import PMOutcomeEmbed from '@components/Admin/PMOutcomes/PMOutcomeEmbed';
import { useParams } from 'react-router-dom';
import { useCurrentSection } from '@reducers/sections/hooks';
import {
  useGetActiveTestSession,
  useGetTestlets,
  useLESkillReport,
  useLELessonReport,
  useLEStandardReport,
} from '../../hooks';
import { ReportType } from './useReportHooks';

/** Whether a learning-outcome report type has ANY data across the section's
 * subjects — empty report types (e.g. skill/lesson for curricula whose items
 * are not aligned to the skills taxonomy) hide their section entirely. */
const useLearningOutcomeHasData = (
  reportType: ReportType,
  subjectIds: number[],
  examId: number | undefined,
): boolean => {
  const enabled = subjectIds.length > 0 && !!examId;
  // These hooks return the computed per-student score data object directly
  const skill = useLESkillReport(examId ?? 0, subjectIds, undefined, {
    enabled: enabled && reportType === 'skill',
  });
  const lesson = useLELessonReport(examId ?? 0, subjectIds, undefined, {
    enabled: enabled && reportType === 'lesson',
  });
  const standard = useLEStandardReport(examId ?? 0, subjectIds, undefined, {
    enabled: enabled && reportType === 'standard',
  });
  const result =
    reportType === 'skill' ? skill : reportType === 'lesson' ? lesson : standard;
  return !!result && Object.keys(result).length > 0;
};

const LearningOutcomesSection = () => {
  const section = useCurrentSection();
  const testSession = useGetActiveTestSession();
  const examId = testSession?.exam?.id;
  const subjectIds = getReportSubjects(section).map((subject) => subject.id);

  const hasSkill = useLearningOutcomeHasData('skill', subjectIds, examId);
  const hasLesson = useLearningOutcomeHasData('lesson', subjectIds, examId);
  const hasStandard = useLearningOutcomeHasData('standard', subjectIds, examId);

  if (!hasSkill && !hasLesson && !hasStandard) {
    return null;
  }

  return (
    <HideEmptyColumnsProvider>
      <SectionWrapper
        title="Learning Outcomes"
        description="Track student progress across different learning objectives, including skills, lessons, and state standards"
        uniqueTestId="learning-outcomes-section"
      >
        <ShowIf If={hasSkill}>
          <SectionWrapper
            title="Learning Outcomes Report - by Skill"
            description="Track student progress across different skills"
            uniqueTestId="skill-report-section"
            action={<ExportCsvButton reportType="skill" />}
          >
            <CurriculumSubjectReportTabs ReportComponent={SkillReport} />
          </SectionWrapper>
        </ShowIf>

        <ShowIf If={hasLesson}>
          <SectionWrapper
            title="Learning Outcomes Report - by Lesson"
            description="Track student progress across different lessons"
            uniqueTestId="lesson-report-section"
            action={<ExportCsvButton reportType="lesson" />}
          >
            <CurriculumSubjectReportTabs ReportComponent={LessonReport} />
          </SectionWrapper>
        </ShowIf>

        <ShowIf If={hasStandard}>
          <SectionWrapper
            title="Learning Outcomes Report - by Standard"
            description="Track student progress across different standards"
            uniqueTestId="standard-report-section"
            action={<ExportCsvButton reportType="standard" />}
          >
            <CurriculumSubjectReportTabs ReportComponent={StandardReport} />
          </SectionWrapper>
        </ShowIf>
      </SectionWrapper>
    </HideEmptyColumnsProvider>
  );
};

const ReportsWrapper = () => {
  const [hideNames, setHideNames] = useState(false);
  const isTeacher = useIsTeacher();
  const testSession = useGetActiveTestSession();
  const { testSessionId } = useParams();
  const { data: testlets } = useGetTestlets(testSessionId);
  // Per-question widgets only make sense for linear testlets; a fully
  // item-adaptive exam hides them entirely
  const hasLinearTestlets = !!testlets?.some((testlet) => !testlet.is_branching);
  const isLearningOutcomeReportsEnabled = useLearningOutcomeReportsAccess({
    suspense: true,
  });

  /**
   * When the teacher wants to hide the student names, we need to hide the first column of the tables.
   * Since the structure of most tables are different, we need to use a selector to target the specific table.
   * And determine the column or cell elements to hide.
   */
  const boxWrapperSx = useMemo((): SxProps<Theme> => {
    const base = { pb: '100px', px: 2, pt: 2 };
    if (!hideNames) {
      return base;
    }
    const sqSelector = '& div[data-testid="ct/score-by-question-section"]';
    const raSelector = '& div[data-testid="ct/response-analysis-section"]';
    const loSelector = '& div[data-testid="ct/learning-outcomes-section"]';
    return {
      ...base,
      [`${sqSelector} th:first-child`]: { display: 'none' },
      [`${sqSelector} td:first-child`]: { display: 'none' },
      [`${raSelector} .lrn-ra-head-name`]: { display: 'none' },
      [`${raSelector} .lrn-ra-body-student`]: { display: 'none' },
      [`${loSelector} th:first-child`]: { display: 'none' },
      [`${loSelector} td:first-child`]: { display: 'none' },
    };
  }, [hideNames]);

  return (
    <HighlightedScoreProvider>
      <TestBreadcrumbsFilter currentPage="Reports" />
      <ScrollWrapper sx={{ width: '100%', height: '100%' }}>
        <Box sx={boxWrapperSx}>
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              mb: 2,
            }}
          >
            <Typography variant="h5" component="h1" sx={{ fontWeight: 'bold' }}>
              Exam Reports
            </Typography>
            <Button
              variant="contained"
              color="primary"
              onClick={() => setHideNames(!hideNames)}
              aria-pressed={hideNames}
              size="small"
              startIcon={hideNames ? <VisibilityOffIcon /> : <VisibilityIcon />}
              sx={{ '& .MuiButton-startIcon': { marginTop: '-2px' } }}
            >
              {hideNames ? 'Show Names' : 'Hide Names'}
            </Button>
          </Box>
          {/* Renders its own section chrome, and nothing at all for non-PM exams */}
          <ShowIf If={isTeacher}>
            <PMOutcomeEmbed examId={testSession?.exam?.id} />
          </ShowIf>

          <ShowIf If={hasLinearTestlets}>
            <SectionWrapper
              title="Score by Question"
              description="High level view of student scores for each question. Green and red represent correct and incorrect, gray represents no score (not attempted or not graded)"
              uniqueTestId="score-by-question-section"
            >
              <TestletReportTabs ReportComponent={LearnosityExamReport} excludeBranching />
            </SectionWrapper>
          </ShowIf>

          <ShowIf If={isTeacher && hasLinearTestlets}>
            <SectionWrapper
              title="Response Analysis"
              description="Deep dive into student responses and answer patterns. Identify common misconceptions and areas for improvement."
              uniqueTestId="response-analysis-section"
            >
              <TestletReportTabs ReportComponent={ResponseAnalysisReport} excludeBranching />
            </SectionWrapper>
          </ShowIf>

          <ShowIf If={isTeacher && isLearningOutcomeReportsEnabled}>
            <LearningOutcomesSection />
          </ShowIf>
        </Box>
      </ScrollWrapper>
    </HighlightedScoreProvider>
  );
};

export default ReportsWrapper;
